import { useState, type JSX } from 'react';
import {
  catalogProfile,
  GENERAL_CATALOG_ATTRIBUTES,
  PROPERTY_CATALOG_ATTRIBUTES,
  type CatalogConfiguration,
  type CatalogAttribute,
} from '@cryptology-assist/domain';
import { Field } from './components';
import type { Copy } from './copy';

function ReferenceTerms({
  terms,
  onChange,
}: {
  terms: string[];
  onChange: (terms: string[]) => void;
}): JSX.Element {
  const [text, setText] = useState(terms.join(', '));
  return (
    <input
      maxLength={500}
      value={text}
      onChange={(event) => {
        const value = event.target.value;
        setText(value);
        onChange(
          value
            .split(',')
            .map((term) => term.trim())
            .filter(Boolean)
        );
      }}
    />
  );
}

export function CatalogSettings({
  value,
  onChange,
  copy,
}: {
  value: CatalogConfiguration | undefined;
  onChange: (value: CatalogConfiguration | undefined) => void;
  copy: Copy;
}): JSX.Element {
  const attributes = value?.attributes.length
    ? value.attributes
    : value?.profile === 'property'
      ? PROPERTY_CATALOG_ATTRIBUTES
      : GENERAL_CATALOG_ATTRIBUTES;
  return (
    <section className="form-section">
      <h2>{copy.catalogSettings}</h2>
      <p>{copy.catalogSettingsHelp}</p>
      <Field label={copy.catalogType}>
        <select
          value={value?.profile ?? ''}
          onChange={(event) => {
            const profile = event.target.value;
            onChange(
              profile === 'property' || profile === 'general'
                ? catalogProfile(profile)
                : undefined
            );
          }}
        >
          <option value="">{copy.catalogSourceDefault}</option>
          <option value="property">{copy.catalogProperty}</option>
          <option value="general">{copy.catalogGeneral}</option>
        </select>
      </Field>
      {value && (
        <>
          <div className="form-grid">
            <Field label={copy.catalogLabel}>
              <input
                required
                maxLength={80}
                value={value.label}
                onChange={(event) =>
                  onChange({ ...value, label: event.target.value })
                }
              />
            </Field>
            <Field label={copy.catalogReferenceTerms}>
              <ReferenceTerms
                key={value.profile}
                terms={value.referenceTerms}
                onChange={(referenceTerms) =>
                  onChange({
                    ...value,
                    referenceTerms,
                  })
                }
              />
            </Field>
          </div>
          <p>{copy.catalogAttributesHelp}</p>
          {value.profile === 'property' ? (
            <div className="checks">
              {PROPERTY_CATALOG_ATTRIBUTES.map((attribute) => (
                <label key={attribute.key}>
                  <input
                    type="checkbox"
                    disabled={attribute.key === 'availability'}
                    checked={attributes.some(
                      (item) => item.key === attribute.key
                    )}
                    onChange={(event) => {
                      const selected = event.target.checked
                        ? [...attributes, attribute]
                        : attributes.filter(
                            (item) => item.key !== attribute.key
                          );
                      if (selected.length)
                        onChange({ ...value, attributes: selected });
                    }}
                  />
                  {attribute.label}
                </label>
              ))}
            </div>
          ) : (
            <>
              {attributes.map((attribute, index) => {
                const update = (patch: Partial<CatalogAttribute>) =>
                  onChange({
                    ...value,
                    attributes: attributes.map((item, position) =>
                      position === index ? { ...item, ...patch } : item
                    ),
                  });
                return (
                  <div className="form-section" key={index}>
                    <div className="form-grid">
                      <Field label={copy.catalogField}>
                        <input
                          required
                          maxLength={80}
                          value={attribute.key}
                          onChange={(event) =>
                            update({ key: event.target.value })
                          }
                        />
                      </Field>
                      <Field label={copy.catalogAttributeLabel}>
                        <input
                          required
                          maxLength={100}
                          value={attribute.label}
                          onChange={(event) =>
                            update({ label: event.target.value })
                          }
                        />
                      </Field>
                      <Field label={copy.catalogValueType}>
                        <select
                          value={attribute.type}
                          onChange={(event) =>
                            update({
                              type: event.target
                                .value as CatalogAttribute['type'],
                            })
                          }
                        >
                          {(
                            [
                              'text',
                              'number',
                              'decimal',
                              'boolean',
                              'text_list',
                              'money',
                            ] as const
                          ).map((type) => (
                            <option key={type} value={type}>
                              {copy[`catalogType_${type}`]}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label={copy.catalogUnit}>
                        <input
                          maxLength={20}
                          value={attribute.unit ?? ''}
                          onChange={(event) =>
                            update({ unit: event.target.value || null })
                          }
                        />
                      </Field>
                    </div>
                    <button
                      type="button"
                      disabled={attributes.length === 1}
                      onClick={() =>
                        onChange({
                          ...value,
                          attributes: attributes.filter(
                            (_item, position) => position !== index
                          ),
                        })
                      }
                    >
                      {copy.catalogRemoveAttribute}
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                disabled={attributes.length >= 30}
                onClick={() =>
                  onChange({
                    ...value,
                    attributes: [
                      ...attributes,
                      { key: '', label: '', type: 'text', unit: null },
                    ],
                  })
                }
              >
                {copy.catalogAddAttribute}
              </button>
            </>
          )}
        </>
      )}
    </section>
  );
}
